/**
 * The filter row above the payment table (R6b).
 *
 * THE SEARCH BOX MAKES A PROMISE AND THE SERVER DECIDES WHETHER IT CAN KEEP IT.
 * R6a searches the resolved invoice's document number and title, and — only when
 * the term is ENTIRELY digits — the provider payment id. It does not search
 * tenant names. A box labelled only "suchen" beside a column headed
 * "Mieter / Objekt" implies otherwise, and an operator who types a tenant's name
 * into it gets an empty table and concludes the payment does not exist. So the
 * placeholder names what IS covered and the hint names what is not.
 *
 * THE MONTH IS THE PAYMENT DATE. This product has at least three other months a
 * reader might assume — the invoice date, the billing period, the sync stamp —
 * and each would give a plausible, wrong answer rather than an empty one. The
 * control says which one it filters.
 *
 * WHAT IS DELIBERATELY ABSENT. No status filter: a payment is a transaction, not
 * a document with a lifecycle, and it has no status to filter by. No tenant
 * filter: R6a accepts one, but this slice has no tenant control and inventing
 * one would mean loading a list of tenants to fill it — a filter is not built
 * because the backend can serve it. No currency filter, and no sum: the mirror
 * is upsert-only and never deletes, so any total over these rows overstates cash.
 *
 * THE EXPORT BUTTON (R8) IS OPTIONAL, AND ITS ABSENCE IS THE ANSWER FOR VANTIO.
 * `availability = "not_recorded"` is not an empty population — it means this
 * source records no individual payments at all — so the page wires no handler
 * and no button appears. A disabled button would say "not right now" where the
 * truth is "not ever, here". A button that IS present but disabled means the
 * population is empty or a run is already going.
 *
 * The layout is `BexioFilterBar` unchanged, the same shell the invoice row and
 * the two Bexio workspace rows use.
 */
import React from "react";
import { Download } from "lucide-react";

import BexioFilterBar from "../../../components/admin/bexio/workspace/BexioFilterBar";
import PeriodPicker from "../uebersicht/PeriodPicker";

/** Exactly what R6a's `q` reaches, and the one thing it does not. */
const SEARCH_PLACEHOLDER = "Beleg-Nr., Titel oder Zahlungs-ID …";
const SEARCH_SCOPE =
  "Die Suche umfasst Beleg-Nr. und Titel der Rechnung sowie die numerische " +
  "Zahlungs-ID, nicht den Mieternamen.";
const PERIOD_SCOPE = "Der Monatsfilter bezieht sich auf das Zahlungsdatum.";

export default function PaymentsFilters({
  q,
  onQChange,
  period,
  onPeriodChange,
  onReset,
  canReset = false,
  resultLabel = "",
  onExport,
  exporting = false,
  canExport = false,
}) {
  return (
    <BexioFilterBar
      searchValue={q}
      onSearchChange={onQChange}
      searchLabel="Zahlungen durchsuchen"
      searchPlaceholder={SEARCH_PLACEHOLDER}
      searchHint={`${SEARCH_SCOPE} ${PERIOD_SCOPE}`}
      onReset={onReset}
      canReset={canReset}
      resultLabel={resultLabel}
    >
      {/* The picker's trigger names the selected range; the group names what the
          range MEANS, so the accessible name is not just "September 2026" — and
          so it cannot be mistaken for the invoice month one view over. */}
      <div
        role="group"
        aria-label="Zahlungsmonat"
        className="inline-flex shrink-0 items-center gap-2 text-xs text-muted-foreground"
      >
        <span className="sr-only sm:not-sr-only">Zahlungsmonat</span>
        <PeriodPicker value={period} onChange={onPeriodChange} />
      </div>

      {/* R9: `aria-busy` because `disabled` removes the button from the tab
          order and says nothing about WHY, and the label change to
          "Exportiere …" is silent to a reader not already focused on it. */}
      {onExport ? (
        <button
          type="button"
          onClick={onExport}
          disabled={exporting || !canExport}
          aria-busy={exporting}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm font-medium text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Download aria-hidden="true" className="h-3.5 w-3.5" />
          {exporting ? "Exportiere …" : "Exportieren"}
        </button>
      ) : null}
    </BexioFilterBar>
  );
}
